import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { 
  Users, Palette, Image, Quote, Building2, Plus, Trash2, Edit2, Save, 
  Upload, Star, X, Linkedin, Mail, Phone, Globe, Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import api from '../utils/api';
import { toFullUrl } from '../utils/imageUrl';

const ProfileEditor = () => {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Team Members
  const [teamMembers, setTeamMembers] = useState([]);
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [memberForm, setMemberForm] = useState({
    name: '', title: '', email: '', phone: '', linkedin_url: '',
    bio: '', department: '', years_experience: '', specializations: '', languages: '',
    is_primary_contact: false, photo_url: ''
  });
  
  // Customization
  const [customization, setCustomization] = useState({
    theme_color: '#0891b2',
    tagline: '',
    about_us: '',
    why_choose_us: '',
    mission_statement: '',
    founded_year: '',
    employee_count: '',
    company_video_url: ''
  });

  // Testimonials
  const [testimonials, setTestimonials] = useState([]);
  const [showTestimonialDialog, setShowTestimonialDialog] = useState(false);
  const [testimonialForm, setTestimonialForm] = useState({
    client_name: '', client_company: '', quote: '', rating: 5
  });

  // Gallery
  const [gallery, setGallery] = useState([]);
  const [showGalleryDialog, setShowGalleryDialog] = useState(false);
  const [galleryForm, setGalleryForm] = useState({
    url: '', caption: '', category: ''
  });

  // Client Logos
  const [clientLogos, setClientLogos] = useState([]);
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [clientForm, setClientForm] = useState({
    name: '', logo_url: '', website: ''
  });

  useEffect(() => {
    fetchCompanyData();
  }, []);

  const fetchCompanyData = async () => {
    try {
      const response = await api.get('/companies/me');
      const data = response.data;
      setCompany(data);
      setTeamMembers(data.team_members || []);
      setTestimonials(data.testimonials || []);
      setGallery(data.gallery || []);
      setClientLogos(data.client_logos || []);
      setCustomization({
        theme_color: data.theme_color || '#0891b2',
        tagline: data.tagline || '',
        about_us: data.about_us || '',
        why_choose_us: data.why_choose_us || '',
        mission_statement: data.mission_statement || '',
        founded_year: data.founded_year || '',
        employee_count: data.employee_count || '',
        company_video_url: data.company_video_url || ''
      });
    } catch (error) {
      toast.error('Failed to load company data');
    } finally {
      setLoading(false);
    }
  };

  // Team Member Functions
  const openTeamDialog = (member = null) => {
    if (member) {
      setEditingMember(member);
      setMemberForm({
        ...member,
        specializations: member.specializations?.join(', ') || '',
        languages: member.languages?.join(', ') || ''
      });
    } else {
      setEditingMember(null);
      setMemberForm({
        name: '', title: '', email: '', phone: '', linkedin_url: '',
        bio: '', department: '', years_experience: '', specializations: '', languages: '',
        is_primary_contact: false, photo_url: ''
      });
    }
    setShowTeamDialog(true);
  };

  const saveTeamMember = async () => {
    if (!memberForm.name || !memberForm.title) {
      toast.error('Name and title are required');
      return;
    }

    const data = {
      ...memberForm,
      years_experience: memberForm.years_experience ? parseInt(memberForm.years_experience) : null,
      specializations: memberForm.specializations ? memberForm.specializations.split(',').map(s => s.trim()) : [],
      languages: memberForm.languages ? memberForm.languages.split(',').map(s => s.trim()) : []
    };

    try {
      if (editingMember) {
        await api.put(`/companies/me/team/${editingMember.id}`, data);
        toast.success('Team member updated');
      } else {
        await api.post('/companies/me/team', data);
        toast.success('Team member added');
      }
      setShowTeamDialog(false);
      fetchCompanyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save team member');
    }
  };

  const deleteTeamMember = async (memberId) => {
    if (!confirm('Are you sure you want to remove this team member?')) return;
    
    try {
      await api.delete(`/companies/me/team/${memberId}`);
      toast.success('Team member removed');
      fetchCompanyData();
    } catch (error) {
      toast.error('Failed to remove team member');
    }
  };

  // Customization Functions
  const saveCustomization = async () => {
    setSaving(true);
    try {
      await api.put('/companies/me/customize', {
        ...customization,
        founded_year: customization.founded_year ? parseInt(customization.founded_year) : null
      });
      toast.success('Profile customization saved');
    } catch (error) {
      toast.error('Failed to save customization');
    } finally {
      setSaving(false);
    }
  };

  // Testimonial Functions
  const saveTestimonial = async () => {
    if (!testimonialForm.client_name || !testimonialForm.quote) {
      toast.error('Client name and quote are required');
      return;
    }

    try {
      await api.post('/companies/me/testimonials', testimonialForm);
      toast.success('Testimonial added');
      setShowTestimonialDialog(false);
      setTestimonialForm({ client_name: '', client_company: '', quote: '', rating: 5 });
      fetchCompanyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add testimonial');
    }
  };

  const deleteTestimonial = async (id) => {
    try {
      await api.delete(`/companies/me/testimonials/${id}`);
      toast.success('Testimonial removed');
      fetchCompanyData();
    } catch (error) {
      toast.error('Failed to remove testimonial');
    }
  };

  // Gallery Functions
  const saveGalleryImage = async () => {
    if (!galleryForm.url) {
      toast.error('Image URL is required');
      return;
    }

    try {
      await api.post('/companies/me/gallery', galleryForm);
      toast.success('Image added to gallery');
      setShowGalleryDialog(false);
      setGalleryForm({ url: '', caption: '', category: '' });
      fetchCompanyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add image');
    }
  };

  const deleteGalleryImage = async (id) => {
    try {
      await api.delete(`/companies/me/gallery/${id}`);
      toast.success('Image removed');
      fetchCompanyData();
    } catch (error) {
      toast.error('Failed to remove image');
    }
  };

  // Client Logo Functions
  const saveClientLogo = async () => {
    if (!clientForm.name) {
      toast.error('Client name is required');
      return;
    }

    try {
      await api.post('/companies/me/clients', clientForm);
      toast.success('Client added');
      setShowClientDialog(false);
      setClientForm({ name: '', logo_url: '', website: '' });
      fetchCompanyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add client');
    }
  };

  const deleteClientLogo = async (id) => {
    try {
      await api.delete(`/companies/me/clients/${id}`);
      toast.success('Client removed');
      fetchCompanyData();
    } catch (error) {
      toast.error('Failed to remove client');
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </>
    );
  }

  const themeColor = customization.theme_color;

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50 py-8">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Profile Editor</h1>
              <p className="text-slate-600">Customize your company&apos;s mini-website</p>
            </div>
            <Button onClick={() => navigate(`/company/${company?.id}`)} variant="outline">
              Preview Profile
            </Button>
          </div>

          <Tabs defaultValue="customize" className="space-y-6">
            <TabsList className="bg-white shadow-sm">
              <TabsTrigger value="customize" className="gap-2">
                <Palette className="h-4 w-4" />
                Customize
              </TabsTrigger>
              <TabsTrigger value="team" className="gap-2">
                <Users className="h-4 w-4" />
                Team ({teamMembers.length}/5)
              </TabsTrigger>
              <TabsTrigger value="gallery" className="gap-2">
                <Image className="h-4 w-4" />
                Gallery ({gallery.length})
              </TabsTrigger>
              <TabsTrigger value="testimonials" className="gap-2">
                <Quote className="h-4 w-4" />
                Testimonials ({testimonials.length})
              </TabsTrigger>
              <TabsTrigger value="clients" className="gap-2">
                <Building2 className="h-4 w-4" />
                Clients ({clientLogos.length})
              </TabsTrigger>
            </TabsList>

            {/* Customize Tab */}
            <TabsContent value="customize">
              <Card>
                <CardHeader>
                  <CardTitle>Profile Customization</CardTitle>
                  <CardDescription>Personalize your company profile appearance and content</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label>Theme Color</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          type="color"
                          value={customization.theme_color}
                          onChange={(e) => setCustomization({...customization, theme_color: e.target.value})}
                          className="w-16 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={customization.theme_color}
                          onChange={(e) => setCustomization({...customization, theme_color: e.target.value})}
                          placeholder="#0891b2"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Tagline / Slogan</Label>
                      <Input
                        value={customization.tagline}
                        onChange={(e) => setCustomization({...customization, tagline: e.target.value})}
                        placeholder="Your trusted freight partner in the Caribbean"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label>Founded Year</Label>
                      <Input
                        type="number"
                        value={customization.founded_year}
                        onChange={(e) => setCustomization({...customization, founded_year: e.target.value})}
                        placeholder="2010"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Employee Count</Label>
                      <Input
                        value={customization.employee_count}
                        onChange={(e) => setCustomization({...customization, employee_count: e.target.value})}
                        placeholder="50-100"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>About Us</Label>
                    <Textarea
                      value={customization.about_us}
                      onChange={(e) => setCustomization({...customization, about_us: e.target.value})}
                      placeholder="Tell visitors about your company, history, and expertise..."
                      className="mt-1 min-h-[120px]"
                    />
                  </div>

                  <div>
                    <Label>Why Choose Us</Label>
                    <Textarea
                      value={customization.why_choose_us}
                      onChange={(e) => setCustomization({...customization, why_choose_us: e.target.value})}
                      placeholder="What makes your company stand out? Your unique value proposition..."
                      className="mt-1 min-h-[100px]"
                    />
                  </div>

                  <div>
                    <Label>Mission Statement</Label>
                    <Textarea
                      value={customization.mission_statement}
                      onChange={(e) => setCustomization({...customization, mission_statement: e.target.value})}
                      placeholder="Your company's mission and core values..."
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Company Video (YouTube/Vimeo URL)</Label>
                    <Input
                      value={customization.company_video_url}
                      onChange={(e) => setCustomization({...customization, company_video_url: e.target.value})}
                      placeholder="https://www.youtube.com/watch?v=..."
                      className="mt-1"
                    />
                  </div>

                  <Button onClick={saveCustomization} disabled={saving} className="w-full" style={{ backgroundColor: themeColor }}>
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Customization'}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Team Tab */}
            <TabsContent value="team">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Team Members</CardTitle>
                    <CardDescription>Add up to 5 key team members to your profile</CardDescription>
                  </div>
                  {teamMembers.length < 5 && (
                    <Button onClick={() => openTeamDialog()} style={{ backgroundColor: themeColor }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Member
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {teamMembers.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p>No team members added yet</p>
                      <p className="text-sm">Add key personnel to showcase your team</p>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      {teamMembers.map((member) => (
                        <div 
                          key={member.id} 
                          className="flex items-start gap-4 p-4 rounded-lg border bg-white"
                        >
                          {member.photo_url ? (
                            <img 
                              src={member.photo_url} 
                              alt={member.name}
                              className="w-16 h-16 rounded-full object-cover"
                            />
                          ) : (
                            <div 
                              className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold"
                              style={{ backgroundColor: themeColor }}
                            >
                              {member.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-slate-900">{member.name}</h4>
                            <p className="text-sm" style={{ color: themeColor }}>{member.title}</p>
                            {member.department && (
                              <Badge variant="outline" className="mt-1 text-xs">{member.department}</Badge>
                            )}
                            <div className="flex gap-2 mt-2">
                              {member.email && <Mail className="h-3 w-3 text-slate-400" />}
                              {member.linkedin_url && <Linkedin className="h-3 w-3 text-slate-400" />}
                            </div>
                          </div>
                          <div className="flex flex-col gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openTeamDialog(member)}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteTeamMember(member.id)} className="text-red-500">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Team Member Dialog */}
              <Dialog open={showTeamDialog} onOpenChange={setShowTeamDialog}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingMember ? 'Edit Team Member' : 'Add Team Member'}</DialogTitle>
                    <DialogDescription>Add details about your team member</DialogDescription>
                  </DialogHeader>
                  <div className="grid md:grid-cols-2 gap-4 py-4">
                    <div>
                      <Label>Name *</Label>
                      <Input
                        value={memberForm.name}
                        onChange={(e) => setMemberForm({...memberForm, name: e.target.value})}
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <Label>Title/Role *</Label>
                      <Input
                        value={memberForm.title}
                        onChange={(e) => setMemberForm({...memberForm, title: e.target.value})}
                        placeholder="Operations Manager"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={memberForm.email}
                        onChange={(e) => setMemberForm({...memberForm, email: e.target.value})}
                        placeholder="john@company.com"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={memberForm.phone}
                        onChange={(e) => setMemberForm({...memberForm, phone: e.target.value})}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                    <div>
                      <Label>Department</Label>
                      <Input
                        value={memberForm.department}
                        onChange={(e) => setMemberForm({...memberForm, department: e.target.value})}
                        placeholder="Sales, Operations, Logistics..."
                      />
                    </div>
                    <div>
                      <Label>Years of Experience</Label>
                      <Input
                        type="number"
                        value={memberForm.years_experience}
                        onChange={(e) => setMemberForm({...memberForm, years_experience: e.target.value})}
                        placeholder="10"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>LinkedIn URL</Label>
                      <Input
                        value={memberForm.linkedin_url}
                        onChange={(e) => setMemberForm({...memberForm, linkedin_url: e.target.value})}
                        placeholder="https://linkedin.com/in/johnsmith"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Photo URL</Label>
                      <Input
                        value={memberForm.photo_url}
                        onChange={(e) => setMemberForm({...memberForm, photo_url: e.target.value})}
                        placeholder="https://example.com/photo.jpg"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Bio</Label>
                      <Textarea
                        value={memberForm.bio}
                        onChange={(e) => setMemberForm({...memberForm, bio: e.target.value})}
                        placeholder="Brief professional background..."
                      />
                    </div>
                    <div>
                      <Label>Specializations (comma-separated)</Label>
                      <Input
                        value={memberForm.specializations}
                        onChange={(e) => setMemberForm({...memberForm, specializations: e.target.value})}
                        placeholder="Air Freight, Customs, FCL..."
                      />
                    </div>
                    <div>
                      <Label>Languages (comma-separated)</Label>
                      <Input
                        value={memberForm.languages}
                        onChange={(e) => setMemberForm({...memberForm, languages: e.target.value})}
                        placeholder="English, Spanish, French..."
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowTeamDialog(false)}>Cancel</Button>
                    <Button onClick={saveTeamMember} style={{ backgroundColor: themeColor }}>
                      {editingMember ? 'Update' : 'Add'} Team Member
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </TabsContent>

            {/* Gallery Tab */}
            <TabsContent value="gallery">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Photo Gallery</CardTitle>
                    <CardDescription>Showcase your facilities, fleet, and team</CardDescription>
                  </div>
                  {gallery.length < 20 && (
                    <Button onClick={() => setShowGalleryDialog(true)} style={{ backgroundColor: themeColor }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Image
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {gallery.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Image className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p>No gallery images yet</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {gallery.map((image) => (
                        <div key={image.id} className="relative group aspect-video rounded-lg overflow-hidden">
                          <img src={image.url} alt={image.caption || 'Gallery'} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button variant="ghost" size="sm" className="text-white" onClick={() => deleteGalleryImage(image.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          {image.category && (
                            <Badge className="absolute top-2 left-2 text-xs">{image.category}</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Dialog open={showGalleryDialog} onOpenChange={setShowGalleryDialog}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Gallery Image</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Image URL *</Label>
                      <Input
                        value={galleryForm.url}
                        onChange={(e) => setGalleryForm({...galleryForm, url: e.target.value})}
                        placeholder="https://example.com/image.jpg"
                      />
                    </div>
                    <div>
                      <Label>Caption</Label>
                      <Input
                        value={galleryForm.caption}
                        onChange={(e) => setGalleryForm({...galleryForm, caption: e.target.value})}
                        placeholder="Our warehouse facility"
                      />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Input
                        value={galleryForm.category}
                        onChange={(e) => setGalleryForm({...galleryForm, category: e.target.value})}
                        placeholder="Warehouse, Fleet, Office, Team..."
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowGalleryDialog(false)}>Cancel</Button>
                    <Button onClick={saveGalleryImage} style={{ backgroundColor: themeColor }}>Add Image</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </TabsContent>

            {/* Testimonials Tab */}
            <TabsContent value="testimonials">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Client Testimonials</CardTitle>
                    <CardDescription>Showcase feedback from your satisfied clients</CardDescription>
                  </div>
                  {testimonials.length < 10 && (
                    <Button onClick={() => setShowTestimonialDialog(true)} style={{ backgroundColor: themeColor }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Testimonial
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {testimonials.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Quote className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p>No testimonials yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {testimonials.map((testimonial) => (
                        <div key={testimonial.id} className="p-4 rounded-lg border bg-white flex gap-4">
                          <Quote className="h-8 w-8 flex-shrink-0" style={{ color: `${themeColor}40` }} />
                          <div className="flex-1">
                            <p className="text-slate-700 italic mb-2">&ldquo;{testimonial.quote}&rdquo;</p>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold">{testimonial.client_name}</p>
                                {testimonial.client_company && (
                                  <p className="text-sm text-slate-500">{testimonial.client_company}</p>
                                )}
                              </div>
                              {testimonial.rating && (
                                <div className="flex gap-1">
                                  {[...Array(5)].map((_, i) => (
                                    <Star key={i} className={`h-4 w-4 ${i < testimonial.rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-200'}`} />
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => deleteTestimonial(testimonial.id)} className="text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Dialog open={showTestimonialDialog} onOpenChange={setShowTestimonialDialog}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Testimonial</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Client Name *</Label>
                      <Input
                        value={testimonialForm.client_name}
                        onChange={(e) => setTestimonialForm({...testimonialForm, client_name: e.target.value})}
                        placeholder="Jane Doe"
                      />
                    </div>
                    <div>
                      <Label>Client Company</Label>
                      <Input
                        value={testimonialForm.client_company}
                        onChange={(e) => setTestimonialForm({...testimonialForm, client_company: e.target.value})}
                        placeholder="ABC Corporation"
                      />
                    </div>
                    <div>
                      <Label>Testimonial Quote *</Label>
                      <Textarea
                        value={testimonialForm.quote}
                        onChange={(e) => setTestimonialForm({...testimonialForm, quote: e.target.value})}
                        placeholder="Their service was exceptional..."
                      />
                    </div>
                    <div>
                      <Label>Rating (1-5)</Label>
                      <div className="flex gap-1 mt-1">
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <button
                            key={rating}
                            type="button"
                            onClick={() => setTestimonialForm({...testimonialForm, rating})}
                            className="focus:outline-none"
                          >
                            <Star className={`h-6 w-6 ${rating <= testimonialForm.rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-200'}`} />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowTestimonialDialog(false)}>Cancel</Button>
                    <Button onClick={saveTestimonial} style={{ backgroundColor: themeColor }}>Add Testimonial</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </TabsContent>

            {/* Clients Tab */}
            <TabsContent value="clients">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Client & Partner Logos</CardTitle>
                    <CardDescription>Display logos of companies you work with</CardDescription>
                  </div>
                  {clientLogos.length < 12 && (
                    <Button onClick={() => setShowClientDialog(true)} style={{ backgroundColor: themeColor }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Client
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {clientLogos.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Building2 className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p>No client logos yet</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                      {clientLogos.map((client) => (
                        <div key={client.id} className="relative group p-4 rounded-lg border bg-white text-center">
                          {toFullUrl(client.logo_url) ? (
                            <img src={toFullUrl(client.logo_url)} alt={client.name} className="max-h-12 mx-auto object-contain" />
                          ) : (
                            <span className="text-sm text-slate-600">{client.name}</span>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => deleteClientLogo(client.id)} 
                            className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 text-red-500 bg-white rounded-full shadow"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Dialog open={showClientDialog} onOpenChange={setShowClientDialog}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Client/Partner</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Company Name *</Label>
                      <Input
                        value={clientForm.name}
                        onChange={(e) => setClientForm({...clientForm, name: e.target.value})}
                        placeholder="Acme Corporation"
                      />
                    </div>
                    <div>
                      <Label>Logo URL</Label>
                      <Input
                        value={clientForm.logo_url}
                        onChange={(e) => setClientForm({...clientForm, logo_url: e.target.value})}
                        placeholder="https://example.com/logo.png"
                      />
                    </div>
                    <div>
                      <Label>Website</Label>
                      <Input
                        value={clientForm.website}
                        onChange={(e) => setClientForm({...clientForm, website: e.target.value})}
                        placeholder="https://acme.com"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowClientDialog(false)}>Cancel</Button>
                    <Button onClick={saveClientLogo} style={{ backgroundColor: themeColor }}>Add Client</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default ProfileEditor;
